import React, { useState, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Badge } from "../components/ui/badge";
import {
  Calculator,
  Info,
  BookOpen,
  Lightbulb,
  Copy,
  Check,
  RotateCcw,
  TrendingUp,
  Eye,
  EyeOff,
  Download,
  Share2,
  Sparkles,
  Zap,
  CheckCircle2,
  XCircle,
  Maximize2,
  ArrowRight,
  Hash,
  Box
} from 'lucide-react';
import "@/App.css";

// Integration calculation with detailed steps
const calculateIntegral = (expression, variable, bounds = null) => {
  const steps = [];
  let current = expression.trim();
  
  const parsePolynomial = (expr) => {
    const termRegex = /([+-]?\d*\.?\d*)\*?([a-z])\^?(\d+)?/gi;
    const constantRegex = /^([+-]?\d+\.?\d*)$/;
    
    const terms = [];
    let match;
    
    expr = expr.replace(/\s/g, '');
    
    while ((match = termRegex.exec(expr)) !== null) {
      const coef = match[1] === '' || match[1] === '+' ? 1 : match[1] === '-' ? -1 : parseFloat(match[1]);
      const variable = match[2];
      const power = match[3] ? parseInt(match[3]) : 1;
      terms.push({ coef, variable, power });
    }
    
    const constMatch = expr.match(constantRegex);
    if (constMatch && terms.length === 0) {
      terms.push({ coef: parseFloat(constMatch[1]), variable: null, power: 0 });
    }
    
    return terms;
  };
  
  if (bounds) {
    steps.push(`Definite Integral: ∫[${bounds.lower} to ${bounds.upper}] (${current}) d${variable}`);
  } else {
    steps.push(`Indefinite Integral: ∫ (${current}) d${variable}`);
  }
  
  steps.push(`\nApplying integration rules:`);
  
  const terms = parsePolynomial(current);
  const integratedTerms = [];
  
  terms.forEach(term => {
    if (term.variable === null || term.power === 0) {
      steps.push(`  • ∫ ${term.coef} d${variable} = ${term.coef}${variable} + C (Integral of constant)`);
      integratedTerms.push({ coef: term.coef, variable: variable, power: 1 });
    } else {
      const newPower = term.power + 1;
      const newCoef = term.coef / newPower;
      steps.push(`  • ∫ ${term.coef}${term.variable}^${term.power} d${variable} = ${newCoef.toFixed(6)}${term.variable}^${newPower} (Power rule: n+1 rule)`);
      integratedTerms.push({ coef: newCoef, variable: term.variable, power: newPower });
    }
  });
  
  // Construct result
  let result = '';
  integratedTerms.forEach((term, idx) => {
    if (term.power === 1) {
      const coefStr = Math.abs(term.coef) === 1 ? '' : Math.abs(term.coef).toFixed(4);
      result += (idx === 0 ? (term.coef < 0 ? '-' : '') : term.coef >= 0 ? ' + ' : ' - ') + coefStr + term.variable;
    } else {
      const coefStr = Math.abs(term.coef).toFixed(4);
      result += (idx === 0 ? (term.coef < 0 ? '-' : '') : term.coef >= 0 ? ' + ' : ' - ') + coefStr + term.variable + '^' + term.power;
    }
  });
  
  let finalValue = null;
  
  if (bounds) {
    steps.push(`\nEvaluating at bounds [${bounds.lower}, ${bounds.upper}]:`);
    
    // Evaluate at upper bound
    let upperSum = 0;
    integratedTerms.forEach(term => {
      const val = term.coef * Math.pow(bounds.upper, term.power);
      upperSum += val;
      steps.push(`  • At ${variable}=${bounds.upper}: ${term.coef.toFixed(4)}(${bounds.upper})^${term.power} = ${val.toFixed(6)}`);
    });
    
    // Evaluate at lower bound
    let lowerSum = 0;
    integratedTerms.forEach(term => {
      const val = term.coef * Math.pow(bounds.lower, term.power);
      lowerSum += val;
      steps.push(`  • At ${variable}=${bounds.lower}: ${term.coef.toFixed(4)}(${bounds.lower})^${term.power} = ${val.toFixed(6)}`);
    });
    
    finalValue = upperSum - lowerSum;
    steps.push(`\nFinal value: F(${bounds.upper}) - F(${bounds.lower}) = ${upperSum.toFixed(6)} - ${lowerSum.toFixed(6)} = ${finalValue.toFixed(6)}`);
  } else {
    result += ' + C';
    steps.push(`\nIndefinite integral result: ${result}`);
  }
  
  return { result, steps, bounds, finalValue };
};

// Integration rules
const integrationRules = [
  {
    name: "Power Rule",
    formula: "∫ x^n dx = x^(n+1)/(n+1) + C",
    example: "∫ x^3 dx = x^4/4 + C",
    description: "Add 1 to the power, divide by the new power (n ≠ -1)",
    restriction: "Valid for all n except n = -1"
  },
  {
    name: "Constant Rule",
    formula: "∫ c dx = cx + C",
    example: "∫ 5 dx = 5x + C",
    description: "The integral of a constant is the constant times x",
    restriction: "c is any constant"
  },
  {
    name: "Constant Multiple Rule",
    formula: "∫ cf(x) dx = c∫f(x) dx",
    example: "∫ 3x^2 dx = 3∫x^2 dx = x^3 + C",
    description: "Constants can be factored out of integrals",
    restriction: "c is any constant"
  },
  {
    name: "Sum Rule",
    formula: "∫ [f(x) + g(x)] dx = ∫f(x) dx + ∫g(x) dx",
    example: "∫ (x^2 + x) dx = x^3/3 + x^2/2 + C",
    description: "The integral of a sum is the sum of integrals",
    restriction: "Both functions must be integrable"
  },
  {
    name: "Exponential Rule",
    formula: "∫ e^x dx = e^x + C",
    example: "∫ e^(2x) dx = (1/2)e^(2x) + C",
    description: "The exponential function integrates to itself",
    restriction: "For e^(ax), divide by coefficient a"
  },
  {
    name: "Logarithm Rule",
    formula: "∫ (1/x) dx = ln|x| + C",
    example: "∫ (1/(2x)) dx = (1/2)ln|x| + C",
    description: "The integral of 1/x is the natural logarithm",
    restriction: "x ≠ 0, use absolute value"
  },
  {
    name: "Trigonometric - Sine",
    formula: "∫ sin(x) dx = -cos(x) + C",
    example: "∫ sin(3x) dx = -(1/3)cos(3x) + C",
    description: "Sine integrates to negative cosine",
    restriction: "Adjust for coefficient of x"
  },
  {
    name: "Trigonometric - Cosine",
    formula: "∫ cos(x) dx = sin(x) + C",
    example: "∫ cos(2x) dx = (1/2)sin(2x) + C",
    description: "Cosine integrates to sine",
    restriction: "Adjust for coefficient of x"
  },
  {
    name: "Substitution Method",
    formula: "∫ f(g(x))g'(x) dx = ∫ f(u) du",
    example: "∫ 2x·e^(x^2) dx, let u=x^2",
    description: "Substitute u = g(x) to simplify the integral",
    restriction: "Must identify u and du correctly"
  },
  {
    name: "Integration by Parts",
    formula: "∫ u dv = uv - ∫ v du",
    example: "∫ x·e^x dx = x·e^x - e^x + C",
    description: "Choose u and dv wisely using LIATE rule",
    restriction: "LIATE: Log, Inverse trig, Algebraic, Trig, Exponential"
  }
];

// Example problems
const exampleProblems = [
  { expr: "3x^2", bounds: null, desc: "Simple indefinite integral" },
  { expr: "x^2 + 2x + 1", bounds: null, desc: "Polynomial integral" },
  { expr: "5x^4 - 3x^2 + x", bounds: { lower: 0, upper: 2 }, desc: "Definite integral" },
  { expr: "2x^3 + 4x", bounds: { lower: 1, upper: 3 }, desc: "Definite polynomial" },
  { expr: "x", bounds: { lower: 0, upper: 5 }, desc: "Area under line" },
  { expr: "4x^2", bounds: { lower: -1, upper: 1 }, desc: "Symmetric bounds" },
];

// Copy Button Component
const CopyButton = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleCopy} title={`Copy ${label}`}>
      <AnimatePresence mode="wait">
        {copied ? (
          <motion.div key="check" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
            <Check className="w-3.5 h-3.5 text-green-500" />
          </motion.div>
        ) : (
          <motion.div key="copy" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
            <Copy className="w-3.5 h-3.5" />
          </motion.div>
        )}
      </AnimatePresence>
    </Button>
  );
};

// Property Card Component
const PropertyCard = ({ label, value, color, icon: Icon, formula }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    className={`rounded-xl p-4 ${color} border`}
  >
    <div className="flex items-start justify-between mb-2">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 opacity-70" />}
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
      </div>
      <CopyButton text={value} label={label} />
    </div>
    <div className="font-mono text-lg font-bold text-foreground mb-1">
      {value}
    </div>
    {formula && (
      <div className="text-xs text-muted-foreground font-medium mt-2 font-mono bg-background/50 px-2 py-1 rounded">
        {formula}
      </div>
    )}
  </motion.div>
);

function IntegralCalculator() {
  const [expression, setExpression] = useState("3x^2 + 2x");
  const [variable, setVariable] = useState("x");
  const [integralType, setIntegralType] = useState("indefinite");
  const [lowerBound, setLowerBound] = useState("0");
  const [upperBound, setUpperBound] = useState("2");
  const [showRules, setShowRules] = useState(true);
  const [showSteps, setShowSteps] = useState(true);
  const [activeTab, setActiveTab] = useState("calculator");
  
  const result = useMemo(() => {
    try {
      const bounds = integralType === 'definite' ? {
        lower: parseFloat(lowerBound) || 0,
        upper: parseFloat(upperBound) || 1
      } : null;
      return calculateIntegral(expression, variable, bounds);
    } catch (e) {
      return null;
    }
  }, [expression, variable, integralType, lowerBound, upperBound]);
  
  const handleReset = useCallback(() => {
    setExpression("3x^2 + 2x");
    setVariable("x");
    setIntegralType("indefinite");
    setLowerBound("0");
    setUpperBound("2");
  }, []);
  
  const loadExample = (expr, bounds) => {
    setExpression(expr);
    if (bounds) {
      setIntegralType('definite');
      setLowerBound(bounds.lower.toString());
      setUpperBound(bounds.upper.toString());
    } else {
      setIntegralType('indefinite');
    }
  };
  
  const handleExport = () => {
    if (!result) return;
    const data = {
      expression,
      variable,
      type: integralType,
      bounds: result.bounds,
      result: result.result,
      finalValue: result.finalValue,
      steps: result.steps,
      timestamp: new Date().toISOString()
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `integral-${Date.now()}.json`;
    a.click();
  };
  
  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <Helmet>
        <title>Integral Calculator - Definite & Indefinite Integrals with Steps</title>
        <meta name="description" content="Calculate definite and indefinite integrals with detailed step-by-step solutions. Supports polynomial integration and area calculations." />
      </Helmet>
      
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center shadow-lg">
              <Maximize2 className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground">Integral Calculator</h1>
              <p className="text-muted-foreground mt-1">Compute definite and indefinite integrals step-by-step</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleExport} disabled={!result} className="gap-2">
              <Download className="w-4 h-4" />
              Export
            </Button>
            <Button variant="outline" size="sm" className="gap-2">
              <Share2 className="w-4 h-4" />
              Share
            </Button>
          </div>
        </div>
        
        {/* Quick Stats Bar */}
        {result && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-2 sm:grid-cols-4 gap-3"
          >
            <Card className="p-3 bg-green-500/5 border-green-500/20">
              <div className="text-xs text-muted-foreground mb-1">Type</div>
              <div className="text-lg font-bold text-green-600 capitalize">{integralType}</div>
            </Card>
            <Card className="p-3 bg-emerald-500/5 border-emerald-500/20">
              <div className="text-xs text-muted-foreground mb-1">Variable</div>
              <div className="text-lg font-bold text-emerald-600">{variable}</div>
            </Card>
            <Card className="p-3 bg-teal-500/5 border-teal-500/20">
              <div className="text-xs text-muted-foreground mb-1">Steps</div>
              <div className="text-lg font-bold text-teal-600">{result.steps.length}</div>
            </Card>
            <Card className="p-3 bg-cyan-500/5 border-cyan-500/20">
              <div className="text-xs text-muted-foreground mb-1">Status</div>
              <div className="text-sm font-bold text-cyan-600">Computed</div>
            </Card>
          </motion.div>
        )}
      </motion.div>
      
      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 h-auto">
          <TabsTrigger value="calculator" className="py-3">Calculator</TabsTrigger>
          <TabsTrigger value="rules" className="py-3">Integration Rules</TabsTrigger>
          <TabsTrigger value="examples" className="py-3">Examples</TabsTrigger>
          <TabsTrigger value="guide" className="py-3">Complete Guide</TabsTrigger>
        </TabsList>
        
        {/* Calculator Tab */}
        <TabsContent value="calculator" className="space-y-6">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Input Panel */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="lg:sticky lg:top-24 lg:self-start space-y-6"
            >
              <Card className="rounded-2xl p-6 border-2 shadow-lg">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-foreground text-lg">Input Function</span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-9 w-9 hover:bg-destructive/10 hover:text-destructive" 
                    onClick={handleReset}
                    title="Reset All"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="space-y-5">
                  <div>
                    <Label className="text-sm mb-2 block font-medium">Function f({variable})</Label>
                    <Input
                      type="text"
                      value={expression}
                      onChange={(e) => setExpression(e.target.value)}
                      className="h-12 text-lg font-mono"
                      placeholder="e.g., 3x^2 + 2x"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Use ^ for powers (e.g., x^2, x^3)
                    </p>
                  </div>
                  
                  <div>
                    <Label className="text-sm mb-2 block font-medium">Variable</Label>
                    <Input
                      type="text"
                      value={variable}
                      onChange={(e) => setVariable(e.target.value)}
                      className="h-12 text-lg font-mono"
                      maxLength={1}
                    />
                  </div>
                  
                  <div>
                    <Label className="text-sm mb-3 block font-medium">Integral Type</Label>
                    <Tabs value={integralType} onValueChange={setIntegralType} className="w-full">
                      <TabsList className="w-full grid grid-cols-2 h-auto">
                        <TabsTrigger value="indefinite" className="py-2.5">
                          Indefinite
                        </TabsTrigger>
                        <TabsTrigger value="definite" className="py-2.5">
                          Definite
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </div>
                  
                  {integralType === 'definite' && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="space-y-4"
                    >
                      <div>
                        <Label className="text-sm mb-2 block font-medium">Lower Bound (a)</Label>
                        <Input
                          type="number"
                          value={lowerBound}
                          onChange={(e) => setLowerBound(e.target.value)}
                          className="h-12 text-lg font-mono"
                          step="0.1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm mb-2 block font-medium">Upper Bound (b)</Label>
                        <Input
                          type="number"
                          value={upperBound}
                          onChange={(e) => setUpperBound(e.target.value)}
                          className="h-12 text-lg font-mono"
                          step="0.1"
                        />
                      </div>
                    </motion.div>
                  )}
                </div>
                
                {/* Validation Status */}
                <div className="mt-6">
                  {result ? (
                    <div className="flex items-center gap-2 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <span className="text-sm font-medium text-green-600">Valid Expression ✓</span>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2 p-3 bg-destructive/10 rounded-lg border border-destructive/20">
                      <XCircle className="w-5 h-5 text-destructive mt-0.5" />
                      <div>
                        <span className="text-sm font-medium text-destructive block">Invalid Expression</span>
                        <span className="text-xs text-destructive/80">Check your function syntax</span>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
              
              {/* Quick Examples */}
              <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-green-500/5 to-emerald-500/5">
                <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-yellow-500" />
                  Quick Examples
                </h3>
                <div className="space-y-2">
                  {exampleProblems.map((ex, idx) => (
                    <Button
                      key={idx}
                      variant="outline"
                      className="w-full justify-start text-left h-auto py-3"
                      onClick={() => loadExample(ex.expr, ex.bounds)}
                    >
                      <div className="flex-1">
                        <div className="font-mono text-sm">
                          {ex.bounds ? `∫[${ex.bounds.lower},${ex.bounds.upper}]` : '∫'} {ex.expr} d{variable}
                        </div>
                        <div className="text-xs text-muted-foreground">{ex.desc}</div>
                      </div>
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  ))}
                </div>
              </Card>
            </motion.div>
            
            {/* Center/Right Column - Results */}
            <div className="lg:col-span-2 space-y-6">
              {/* Result Display */}
              {result && (
                <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-emerald-500/5 to-teal-500/5">
                  <h2 className="font-semibold text-foreground flex items-center gap-2 mb-5 text-lg">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    Result
                  </h2>
                  
                  <div className="space-y-4">
                    <div className="p-6 bg-background/60 rounded-xl border-2">
                      <div className="text-sm text-muted-foreground mb-2">Original Function:</div>
                      <div className="font-mono text-xl font-bold text-foreground mb-4">
                        {integralType === 'definite' && result.bounds && `∫[${result.bounds.lower} to ${result.bounds.upper}] `}
                        {integralType === 'indefinite' && '∫ '}
                        ({expression}) d{variable}
                      </div>
                      
                      <div className="text-sm text-muted-foreground mb-2">
                        {integralType === 'indefinite' ? 'Indefinite Integral:' : 'Antiderivative:'}
                      </div>
                      <div className="font-mono text-2xl font-bold text-green-600">
                        {result.result}
                      </div>
                      
                      {result.finalValue !== null && (
                        <div className="mt-4 pt-4 border-t">
                          <div className="text-sm text-muted-foreground mb-2">Definite Integral Value:</div>
                          <div className="font-mono text-3xl font-bold text-emerald-600">
                            {result.finalValue.toFixed(6)}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <CopyButton text={result.finalValue !== null ? result.finalValue.toString() : result.result} label="result" />
                      <Button variant="outline" size="sm" className="gap-2">
                        <Download className="w-3 h-3" />
                        Save Result
                      </Button>
                    </div>
                  </div>
                </Card>
              )}
              
              {/* Visualization for Definite Integrals */}
              {result && result.bounds && (
                <Card className="rounded-2xl p-6 border-2 shadow-lg">
                  <h2 className="font-semibold text-foreground flex items-center gap-2 mb-5 text-lg">
                    <Box className="w-5 h-5 text-blue-600" />
                    Area Interpretation
                  </h2>
                  
                  <div className="p-4 bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-xl border">
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Lower Bound (a):</span>
                        <span className="font-mono font-bold">{result.bounds.lower}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Upper Bound (b):</span>
                        <span className="font-mono font-bold">{result.bounds.upper}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Interval Width:</span>
                        <span className="font-mono font-bold">{(result.bounds.upper - result.bounds.lower).toFixed(2)}</span>
                      </div>
                      <div className="pt-3 border-t flex justify-between items-center">
                        <span className="font-semibold">Total Area:</span>
                        <span className="font-mono text-xl font-bold text-green-600">{result.finalValue?.toFixed(6)}</span>
                      </div>
                    </div>
                    
                    <div className="mt-4 p-3 bg-background/60 rounded text-xs text-muted-foreground">
                      <strong>Interpretation:</strong> The definite integral represents the signed area between the curve f({variable}) = {expression} and the {variable}-axis from {variable} = {result.bounds.lower} to {variable} = {result.bounds.upper}.
                      {result.finalValue && result.finalValue < 0 && " (Negative area indicates the curve is below the axis.)"}
                    </div>
                  </div>
                </Card>
              )}
              
              {/* Step-by-Step Solution */}
              {result && (
                <Card className="rounded-2xl p-6 border-2 shadow-lg">
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="font-semibold text-foreground flex items-center gap-2 text-lg">
                      <BookOpen className="w-5 h-5 text-green-600" />
                      Step-by-Step Solution
                      <Sparkles className="w-4 h-4 text-yellow-500 ml-auto" />
                    </h2>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowSteps(!showSteps)}
                      className="gap-2"
                    >
                      {showSteps ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                      <span className="hidden sm:inline">{showSteps ? 'Hide' : 'Show'}</span>
                    </Button>
                  </div>
                  
                  <AnimatePresence>
                    {showSteps && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="space-y-3"
                      >
                        {result.steps.map((step, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.03 }}
                            className="flex gap-3 p-3 bg-muted/30 rounded-lg border"
                          >
                            <div className="flex-shrink-0 w-7 h-7 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold">
                              {index + 1}
                            </div>
                            <div className="flex-1">
                              <p className="text-sm text-foreground font-mono whitespace-pre-line">{step}</p>
                            </div>
                          </motion.div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Card>
              )}
              
              {/* Additional Information */}
              <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-purple-500/5 to-pink-500/5">
                <h2 className="font-semibold text-foreground flex items-center gap-2 mb-5 text-lg">
                  <Info className="w-5 h-5 text-purple-600" />
                  Understanding Integrals
                </h2>
                
                <div className="space-y-4 text-sm text-muted-foreground">
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">What is Integration?</h3>
                    <p className="leading-relaxed">
                      Integration is the reverse process of differentiation. It finds the original function given its rate of change (derivative). 
                      Geometrically, it calculates the area under a curve.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Types of Integrals</h3>
                    <ul className="space-y-2 list-disc list-inside">
                      <li><strong>Indefinite Integral:</strong> ∫f(x)dx = F(x) + C - Represents a family of functions</li>
                      <li><strong>Definite Integral:</strong> ∫[a,b]f(x)dx - Gives a numerical value (area)</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">The Constant of Integration</h3>
                    <p className="leading-relaxed">
                      When computing indefinite integrals, we add "+ C" because there are infinitely many antiderivatives that differ by a constant. 
                      For definite integrals, the constant cancels out: F(b) - F(a).
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </TabsContent>
        
        {/* Integration Rules Tab */}
        <TabsContent value="rules" className="space-y-6">
          <Card className="rounded-2xl p-6 border-2 shadow-lg">
            <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-green-600" />
              Complete Integration Rules Reference
            </h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              {integrationRules.map((rule, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="p-5 rounded-xl border-2 bg-gradient-to-br from-green-500/5 to-emerald-500/5 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-bold text-lg text-foreground">{rule.name}</h3>
                    <Badge variant="secondary" className="text-xs">{idx + 1}</Badge>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="bg-background/60 p-3 rounded-lg">
                      <div className="text-xs text-muted-foreground mb-1">Formula:</div>
                      <code className="font-mono text-sm font-bold">{rule.formula}</code>
                    </div>
                    
                    <div className="bg-background/60 p-3 rounded-lg">
                      <div className="text-xs text-muted-foreground mb-1">Example:</div>
                      <code className="font-mono text-sm">{rule.example}</code>
                    </div>
                    
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {rule.description}
                    </p>
                    
                    <div className="text-xs text-amber-600 bg-amber-500/10 p-2 rounded">
                      <strong>Note:</strong> {rule.restriction}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </Card>
          
          {/* Fundamental Theorem of Calculus */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-blue-500/5 to-purple-500/5">
            <h2 className="text-xl font-bold text-foreground mb-5 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-500" />
              Fundamental Theorem of Calculus
            </h2>
            
            <div className="space-y-4">
              <div className="p-5 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-3 text-blue-600">Part 1 - The Connection</h3>
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    If f is continuous on [a, b] and F is an antiderivative of f on [a, b], then:
                  </p>
                  <code className="font-mono text-lg block p-3 bg-blue-500/10 rounded text-center">
                    ∫[a to b] f(x)dx = F(b) - F(a)
                  </code>
                  <p className="text-xs text-muted-foreground mt-3">
                    This tells us we can evaluate definite integrals by finding any antiderivative and computing the difference at the bounds.
                  </p>
                </div>
              </div>
              
              <div className="p-5 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-3 text-purple-600">Part 2 - The Derivative of an Integral</h3>
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    If f is continuous on [a, b], then the function F defined by:
                  </p>
                  <code className="font-mono text-lg block p-3 bg-purple-500/10 rounded text-center">
                    F(x) = ∫[a to x] f(t)dt
                  </code>
                  <p className="text-sm text-muted-foreground mt-2">is continuous on [a, b], differentiable on (a, b), and:</p>
                  <code className="font-mono text-lg block p-3 bg-purple-500/10 rounded text-center mt-2">
                    F'(x) = f(x)
                  </code>
                  <p className="text-xs text-muted-foreground mt-3">
                    This shows that differentiation and integration are inverse operations.
                  </p>
                </div>
              </div>
              
              <div className="p-4 bg-gradient-to-r from-green-500/10 to-blue-500/10 rounded-lg border border-green-500/20">
                <strong className="text-sm">Key Insight:</strong>
                <p className="text-xs text-muted-foreground mt-2">
                  The Fundamental Theorem bridges differential calculus (derivatives) and integral calculus (integrals), showing they are inverse processes. 
                  This is one of the most important theorems in all of mathematics.
                </p>
              </div>
            </div>
          </Card>
          
          {/* Advanced Techniques */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-orange-500/5 to-red-500/5">
            <h2 className="text-xl font-bold text-foreground mb-5 flex items-center gap-2">
              <Zap className="w-5 h-5 text-orange-600" />
              Advanced Integration Techniques
            </h2>
            
            <div className="space-y-4">
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2">Integration by Substitution (u-substitution)</h3>
                <div className="text-sm space-y-2">
                  <p className="text-muted-foreground">When you have a composite function f(g(x))·g'(x):</p>
                  <ol className="list-decimal list-inside space-y-1 ml-2">
                    <li>Choose u = g(x)</li>
                    <li>Compute du = g'(x)dx</li>
                    <li>Substitute to get ∫f(u)du</li>
                    <li>Integrate with respect to u</li>
                    <li>Substitute back x for u</li>
                  </ol>
                  <div className="bg-orange-500/10 p-3 rounded mt-2 font-mono text-xs">
                    Example: ∫2x·e^(x²)dx, let u=x², du=2xdx → ∫e^u du = e^u + C = e^(x²) + C
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2">Integration by Parts</h3>
                <div className="text-sm space-y-2">
                  <p className="text-muted-foreground">For products of functions, use: ∫u dv = uv - ∫v du</p>
                  <div className="bg-background p-3 rounded mt-2">
                    <strong>LIATE Rule</strong> (choose u in this order):
                    <ul className="list-disc list-inside ml-2 mt-1 space-y-0.5">
                      <li><strong>L</strong>ogarithmic functions</li>
                      <li><strong>I</strong>nverse trigonometric functions</li>
                      <li><strong>A</strong>lgebraic functions (polynomials)</li>
                      <li><strong>T</strong>rigonometric functions</li>
                      <li><strong>E</strong>xponential functions</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2">Partial Fractions</h3>
                <div className="text-sm text-muted-foreground">
                  Decompose rational functions into simpler fractions that can be integrated individually. 
                  Useful when integrating P(x)/Q(x) where degree of P &lt; degree of Q.
                </div>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2">Trigonometric Substitution</h3>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p>Used for integrals containing:</p>
                  <ul className="list-disc list-inside ml-2">
                    <li>√(a²-x²) → use x = a·sin(θ)</li>
                    <li>√(a²+x²) → use x = a·tan(θ)</li>
                    <li>√(x²-a²) → use x = a·sec(θ)</li>
                  </ul>
                </div>
              </div>
            </div>
          </Card>
        </TabsContent>
        
        {/* Examples Tab */}
        <TabsContent value="examples" className="space-y-6">
          <Card className="rounded-2xl p-6 border-2 shadow-lg">
            <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
              <Lightbulb className="w-6 h-6 text-yellow-500" />
              Worked Examples
            </h2>
            
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="ex-1">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">Example 1: Simple Polynomial - Indefinite</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                    <div>
                      <strong>Problem:</strong> Find ∫(3x² + 2x)dx
                    </div>
                    <div>
                      <strong>Solution:</strong>
                      <ol className="list-decimal list-inside space-y-2 mt-2 ml-2">
                        <li>Apply power rule to 3x²: ∫3x²dx = 3·(x³/3) = x³</li>
                        <li>Apply power rule to 2x: ∫2xdx = 2·(x²/2) = x²</li>
                        <li>Add constant of integration: + C</li>
                        <li>Combine results: x³ + x² + C</li>
                      </ol>
                    </div>
                    <div className="bg-green-500/10 p-3 rounded border border-green-500/20">
                      <strong className="text-green-600">Answer:</strong> <code className="font-mono ml-2">x³ + x² + C</code>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="ex-2">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">Example 2: Definite Integral</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                    <div>
                      <strong>Problem:</strong> Find ∫[0 to 2] x² dx
                    </div>
                    <div>
                      <strong>Solution:</strong>
                      <ol className="list-decimal list-inside space-y-2 mt-2 ml-2">
                        <li>Find antiderivative: F(x) = x³/3</li>
                        <li>Evaluate at upper bound: F(2) = 2³/3 = 8/3</li>
                        <li>Evaluate at lower bound: F(0) = 0³/3 = 0</li>
                        <li>Compute difference: F(2) - F(0) = 8/3 - 0 = 8/3</li>
                      </ol>
                    </div>
                    <div className="bg-green-500/10 p-3 rounded border border-green-500/20">
                      <strong className="text-green-600">Answer:</strong> <code className="font-mono ml-2">8/3 ≈ 2.667</code>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="ex-3">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">Example 3: Higher Degree Polynomial</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                    <div>
                      <strong>Problem:</strong> Find ∫(5x⁴ - 3x² + x)dx
                    </div>
                    <div>
                      <strong>Solution:</strong>
                      <ol className="list-decimal list-inside space-y-2 mt-2 ml-2">
                        <li>∫5x⁴dx = 5·(x⁵/5) = x⁵</li>
                        <li>∫-3x²dx = -3·(x³/3) = -x³</li>
                        <li>∫xdx = x²/2</li>
                        <li>Add constant: + C</li>
                        <li>Combine: x⁵ - x³ + x²/2 + C</li>
                      </ol>
                    </div>
                    <div className="bg-green-500/10 p-3 rounded border border-green-500/20">
                      <strong className="text-green-600">Answer:</strong> <code className="font-mono ml-2">x⁵ - x³ + x²/2 + C</code>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="ex-4">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">Example 4: Area Between Curve and Axis</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                    <div>
                      <strong>Problem:</strong> Find the area under y = 4x from x = 0 to x = 3
                    </div>
                    <div>
                      <strong>Solution:</strong>
                      <ol className="list-decimal list-inside space-y-2 mt-2 ml-2">
                        <li>Set up integral: ∫[0 to 3] 4x dx</li>
                        <li>Find antiderivative: F(x) = 4·(x²/2) = 2x²</li>
                        <li>Evaluate: F(3) = 2(3)² = 18</li>
                        <li>Evaluate: F(0) = 2(0)² = 0</li>
                        <li>Area = F(3) - F(0) = 18</li>
                      </ol>
                    </div>
                    <div className="bg-green-500/10 p-3 rounded border border-green-500/20">
                      <strong className="text-green-600">Answer:</strong> <code className="font-mono ml-2">18 square units</code>
                    </div>
                    <div className="mt-3 text-xs text-muted-foreground">
                      This represents the area of the region bounded by the line y=4x, the x-axis, and the vertical lines x=0 and x=3.
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="ex-5">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">Example 5: Symmetric Bounds</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                    <div>
                      <strong>Problem:</strong> Find ∫[-2 to 2] x³ dx
                    </div>
                    <div>
                      <strong>Solution:</strong>
                      <ol className="list-decimal list-inside space-y-2 mt-2 ml-2">
                        <li>Find antiderivative: F(x) = x⁴/4</li>
                        <li>Evaluate: F(2) = 2⁴/4 = 16/4 = 4</li>
                        <li>Evaluate: F(-2) = (-2)⁴/4 = 16/4 = 4</li>
                        <li>Result: F(2) - F(-2) = 4 - 4 = 0</li>
                      </ol>
                    </div>
                    <div className="bg-green-500/10 p-3 rounded border border-green-500/20">
                      <strong className="text-green-600">Answer:</strong> <code className="font-mono ml-2">0</code>
                    </div>
                    <div className="mt-3 text-xs text-muted-foreground bg-blue-500/10 p-3 rounded">
                      <strong>Note:</strong> This is an odd function integrated over a symmetric interval. The area above the x-axis (positive) cancels with the area below (negative).
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Card>
          
          {/* Practice Problems */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-orange-500/5 to-red-500/5">
            <h2 className="text-xl font-bold text-foreground mb-5 flex items-center gap-2">
              <Hash className="w-5 h-5 text-orange-600" />
              Practice Problems
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                "∫(x² + 3x - 1)dx",
                "∫[0 to 1] 2x dx",
                "∫(4x³ - 2x)dx",
                "∫[-1 to 1] x² dx",
                "∫(x⁴ + x²)dx",
                "∫[2 to 5] 3x² dx",
                "∫(6x⁵ - 4x³ + 2x)dx",
                "∫[0 to 3] (x² + 1)dx"
              ].map((problem, idx) => (
                <div key={idx} className="p-4 bg-background/60 rounded-lg border hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-orange-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {idx + 1}
                    </div>
                    <div className="font-mono text-sm">{problem}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
        
        {/* Complete Guide Tab */}
        <TabsContent value="guide" className="space-y-6">
          {/* FAQ Section */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg">
            <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
              <Lightbulb className="w-6 h-6 text-yellow-500" />
              Frequently Asked Questions
            </h2>
            
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="faq-1">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">Why do we add "+ C" to indefinite integrals?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <p className="text-muted-foreground leading-relaxed">
                      The constant C represents the fact that there are infinitely many antiderivatives of any given function, 
                      all differing by a constant. Since the derivative of a constant is zero, any constant can be added to an antiderivative.
                    </p>
                    <div className="bg-blue-500/10 p-4 rounded-lg border border-blue-500/20">
                      <strong className="text-blue-600">Example:</strong>
                      <p className="text-sm mt-2">All of these functions have the same derivative (2x):</p>
                      <ul className="list-disc list-inside space-y-1 mt-2 text-sm font-mono">
                        <li>F(x) = x²</li>
                        <li>F(x) = x² + 5</li>
                        <li>F(x) = x² - 3</li>
                        <li>F(x) = x² + C (general form)</li>
                      </ul>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      For definite integrals, the constant cancels out: (F(b) + C) - (F(a) + C) = F(b) - F(a)
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-2">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">What does a definite integral represent geometrically?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <p className="text-muted-foreground leading-relaxed">
                      A definite integral ∫[a to b] f(x)dx represents the signed area between the curve y=f(x) and the x-axis, 
                      from x=a to x=b.
                    </p>
                    <div className="space-y-3">
                      <div className="bg-green-500/10 p-3 rounded-lg border border-green-500/20">
                        <strong className="text-green-600">Positive Area:</strong>
                        <p className="text-sm mt-1">When f(x) &gt; 0, the integral counts the area as positive (above x-axis)</p>
                      </div>
                      <div className="bg-red-500/10 p-3 rounded-lg border border-red-500/20">
                        <strong className="text-red-600">Negative Area:</strong>
                        <p className="text-sm mt-1">When f(x) &lt; 0, the integral counts the area as negative (below x-axis)</p>
                      </div>
                      <div className="bg-blue-500/10 p-3 rounded-lg border border-blue-500/20">
                        <strong className="text-blue-600">Net Area:</strong>
                        <p className="text-sm mt-1">The integral gives the net signed area (positive area - negative area)</p>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-3">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">How do I know if a function is integrable?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <p className="text-muted-foreground leading-relaxed">
                      A function is integrable (Riemann integrable) if it satisfies certain conditions:
                    </p>
                    <div className="space-y-2">
                      <div className="p-3 bg-background/60 rounded-lg border">
                        <strong className="text-sm">Continuous Functions:</strong>
                        <p className="text-sm text-muted-foreground mt-1">
                          If f is continuous on [a, b], then it's definitely integrable on [a, b].
                        </p>
                      </div>
                      <div className="p-3 bg-background/60 rounded-lg border">
                        <strong className="text-sm">Bounded with Finite Discontinuities:</strong>
                        <p className="text-sm text-muted-foreground mt-1">
                          If f is bounded and has only finitely many discontinuities on [a, b], it's integrable.
                        </p>
                      </div>
                      <div className="p-3 bg-background/60 rounded-lg border">
                        <strong className="text-sm">Monotonic Functions:</strong>
                        <p className="text-sm text-muted-foreground mt-1">
                          If f is monotonic (always increasing or always decreasing) on [a, b], it's integrable.
                        </p>
                      </div>
                    </div>
                    <div className="bg-amber-500/10 p-3 rounded border border-amber-500/20 mt-3">
                      <strong className="text-amber-600 text-sm">Common Non-Integrable Function:</strong>
                      <p className="text-xs text-muted-foreground mt-1">
                        The Dirichlet function (1 for rationals, 0 for irrationals) is not Riemann integrable because it's too discontinuous.
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-4">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">What's the difference between antiderivative and integral?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <p className="text-muted-foreground leading-relaxed">
                      These terms are closely related but have subtle differences:
                    </p>
                    <div className="space-y-3">
                      <div className="p-4 bg-background/60 rounded-lg border">
                        <strong>Antiderivative:</strong>
                        <p className="text-sm text-muted-foreground mt-2">
                          A function F(x) whose derivative is f(x). It's the function itself, not a process or operation.
                        </p>
                        <code className="text-xs font-mono block mt-2 p-2 bg-background rounded">If F'(x) = f(x), then F is an antiderivative of f</code>
                      </div>
                      <div className="p-4 bg-background/60 rounded-lg border">
                        <strong>Indefinite Integral:</strong>
                        <p className="text-sm text-muted-foreground mt-2">
                          The notation ∫f(x)dx represents the family of all antiderivatives of f. It's the general antiderivative plus C.
                        </p>
                        <code className="text-xs font-mono block mt-2 p-2 bg-background rounded">∫f(x)dx = F(x) + C</code>
                      </div>
                      <div className="p-4 bg-background/60 rounded-lg border">
                        <strong>Definite Integral:</strong>
                        <p className="text-sm text-muted-foreground mt-2">
                          The notation ∫[a to b]f(x)dx is a number representing signed area. It's computed using antiderivatives via FTC.
                        </p>
                        <code className="text-xs font-mono block mt-2 p-2 bg-background rounded">∫[a to b]f(x)dx = F(b) - F(a)</code>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-5">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">How do I choose between substitution and integration by parts?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <p className="text-muted-foreground leading-relaxed">
                      Choosing the right technique is crucial for solving integrals efficiently:
                    </p>
                    <div className="space-y-3">
                      <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                        <strong className="text-green-600">Use Substitution When:</strong>
                        <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
                          <li>You see a function and its derivative together (or close)</li>
                          <li>There's a composite function f(g(x)) where g'(x) appears</li>
                          <li>You have ∫f(ax+b)dx (linear substitution)</li>
                          <li>Example: ∫2x·e^(x²)dx - the 2x is derivative of x²</li>
                        </ul>
                      </div>
                      <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                        <strong className="text-blue-600">Use Integration by Parts When:</strong>
                        <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
                          <li>You have a product of two different types of functions</li>
                          <li>One function becomes simpler when differentiated</li>
                          <li>Use LIATE to choose u: Logarithmic, Inverse trig, Algebraic, Trig, Exponential</li>
                          <li>Example: ∫x·e^x dx - x becomes simpler when differentiated</li>
                        </ul>
                      </div>
                    </div>
                    <div className="bg-purple-500/10 p-3 rounded border border-purple-500/20 mt-3">
                      <strong className="text-sm">Pro Tip:</strong>
                      <p className="text-xs text-muted-foreground mt-1">
                        Sometimes you need to try both methods! If one leads to a more complicated integral, backtrack and try the other. 
                        Experience helps you develop intuition for which method to use.
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Card>
          
          {/* Real-World Applications */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-green-500/5 to-emerald-500/5">
            <h2 className="text-xl font-bold text-foreground mb-5 flex items-center gap-2">
              <Zap className="w-5 h-5 text-green-600" />
              Real-World Applications of Integration
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  Physics - Motion & Dynamics
                </h3>
                <p className="text-xs text-muted-foreground">
                  Finding displacement from velocity, work done by a variable force, center of mass, 
                  moment of inertia, and electric field calculations.
                </p>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <Calculator className="w-4 h-4 text-blue-600" />
                  Economics & Business
                </h3>
                <p className="text-xs text-muted-foreground">
                  Total cost from marginal cost, consumer surplus, present value of continuous income streams, 
                  and Gini coefficient for income inequality.
                </p>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <Box className="w-4 h-4 text-purple-600" />
                  Engineering
                </h3>
                <p className="text-xs text-muted-foreground">
                  Calculating volumes of irregular objects, fluid flow rates, stress and strain analysis, 
                  signal processing, and control system design.
                </p>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <Info className="w-4 h-4 text-orange-600" />
                  Probability & Statistics
                </h3>
                <p className="text-xs text-muted-foreground">
                  Computing probabilities from probability density functions, expected values, 
                  variance, and cumulative distribution functions.
                </p>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <Sparkles className="w-4 h-4 text-cyan-600" />
                  Computer Graphics
                </h3>
                <p className="text-xs text-muted-foreground">
                  Surface area calculations, volume rendering, curve lengths for splines, 
                  and area calculations for filling algorithms.
                </p>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <Lightbulb className="w-4 h-4 text-yellow-600" />
                  Medicine & Biology
                </h3>
                <p className="text-xs text-muted-foreground">
                  Drug concentration over time (pharmacokinetics), total blood flow, 
                  cardiac output, and population models.
                </p>
              </div>
            </div>
          </Card>
          
          {/* Properties of Integrals */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-blue-500/5 to-purple-500/5">
            <h2 className="text-xl font-bold text-foreground mb-5 flex items-center gap-2">
              <Info className="w-5 h-5 text-blue-600" />
              Important Properties of Integrals
            </h2>
            
            <div className="space-y-4">
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2">Linearity</h3>
                <code className="text-sm font-mono block">∫[a,f(x) + bg(x)]dx = a∫f(x)dx + b∫g(x)dx</code>
                <p className="text-xs text-muted-foreground mt-2">Constants factor out and integrals distribute over addition/subtraction</p>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2">Additivity Over Intervals</h3>
                <code className="text-sm font-mono block">∫[a to b]f(x)dx + ∫[b to c]f(x)dx = ∫[a to c]f(x)dx</code>
                <p className="text-xs text-muted-foreground mt-2">Can split integration interval at any point</p>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2">Reversal of Limits</h3>
                <code className="text-sm font-mono block">∫[a to b]f(x)dx = -∫[b to a]f(x)dx</code>
                <p className="text-xs text-muted-foreground mt-2">Swapping limits changes the sign</p>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2">Zero-Width Interval</h3>
                <code className="text-sm font-mono block">∫[a to a]f(x)dx = 0</code>
                <p className="text-xs text-muted-foreground mt-2">Integration over a point gives zero</p>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2">Comparison Property</h3>
                <code className="text-sm font-mono block">If f(x) ≤ g(x) on [a,b], then ∫[a to b]f(x)dx ≤ ∫[a to b]g(x)dx</code>
                <p className="text-xs text-muted-foreground mt-2">Larger function gives larger integral</p>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
export default IntegralCalculator;